/** Filter + dedup tool_calls for inline-render blocks in MessageBubble.
 *  Two rules apply per inline-render family (GIS map, Mermaid):
 *
 *  1. Keep only successful results matching the family's duck-type predicate.
 *
 *  2. When the SAME tool_name appears multiple times in one turn, keep
 *     only the LAST. 2026-05-03 trace: flash-lite called geojson_create
 *     twice — a botched first call (3 features, malformed args, no
 *     autogeocode → empty map) then a self-corrected second call
 *     (50 features, autogeocode=true). Without dedup, the user saw
 *     both maps. Different tools (e.g. geojson_create + geojson_
 *     convex_hull) pass through unchanged — only same-tool retries
 *     collapse.
 *
 *  2026-05-03 P0 follow-up: same retry pattern bit mindmap (list[str] →
 *  list[dict] retry round-trip). Generalized to also cover any tool
 *  result with a `mermaid_code` field (mermaid_tool, mindmap_tool, and
 *  any future Mermaid producers). Each family stays a separate dedup
 *  call so ordering / placement in the bubble can be controlled per family.
 */

type InnerPredicate = (inner: any) => boolean;

function _innerOf(toolCall: any): any {
  const result = toolCall?.result;
  return (result && typeof result === 'object' && 'result' in result)
    ? (result as any).result
    : result;
}

/** Generic same-tool-retry collapse keyed off a per-family duck-type. */
function dedupSameToolRetries(toolCalls: any[], matches: InnerPredicate): any[] {
  const matching = (toolCalls || []).filter((tc: any) => {
    const inner = _innerOf(tc);
    return inner && inner.success === true && matches(inner);
  });
  const lastByTool = new Map<string, any>();
  for (const tc of matching) {
    const name = (tc?.tool_name || tc?.name || 'unknown') as string;
    lastByTool.set(name, tc);
  }
  return Array.from(lastByTool.values());
}

/** Duck-type: GIS-shaped tool result (inline FC or digest envelope). */
const _isGisInner: InnerPredicate = (inner) =>
  inner.geojson != null || inner.ephemeral_geojson_id != null;

/** Duck-type: Mermaid-shaped tool result (mermaid + mindmap producers). */
const _isMermaidInner: InnerPredicate = (inner) =>
  typeof inner.mermaid_code === 'string' && inner.mermaid_code.length > 0;

export function dedupGisToolCalls(toolCalls: any[]): any[] {
  return dedupSameToolRetries(toolCalls, _isGisInner);
}

export function dedupMermaidToolCalls(toolCalls: any[]): any[] {
  return dedupSameToolRetries(toolCalls, _isMermaidInner);
}
